import { DragEvent, PointerEvent, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Button,
    Link,
    makeStyles,
    mergeClasses,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Spinner,
    Text,
    tokens,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import { Case, CaseStatus, CaseSystemStatus } from '../../../../data/types';
import { Column, Paths, Row } from '../../../common';
import { appTokens } from '../../../common/Styles/appTokens';

const systemStatusHeaderColors: Partial<Record<CaseSystemStatus, string>> = {
    [CaseSystemStatus.new]: tokens.colorPaletteTealBackground2,
    [CaseSystemStatus.active]: tokens.colorPaletteBlueBackground2,
    [CaseSystemStatus.completed]: tokens.colorPaletteGreenBackground2,
    [CaseSystemStatus.closed]: tokens.colorPaletteCranberryBackground2,
};

const useStyles = makeStyles({
    board: {
        cursor: 'grab',
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        padding: tokens.spacingVerticalXS,
        scrollbarWidth: 'thin',
        touchAction: 'pan-y',
    },
    boardDragging: {
        cursor: 'grabbing',
        userSelect: 'none',
    },
    column: {
        backgroundColor: tokens.colorNeutralBackground3,
        border: `${tokens.strokeWidthThin} solid ${appTokens.border.colors.subtle}`,
        borderRadius: '5px', //tokens.borderRadiusMedium,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flex: '0 0 18rem',
        maxHeight: '100%',
        minWidth: '18rem',
        overflow: 'hidden',
        transition:
            'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, filter 120ms ease, opacity 120ms ease',
    },
    columnDisabled: {
        filter: 'grayscale(75%)',
        opacity: 0.45,
    },
    columnDragOver: {
        backgroundColor: tokens.colorBrandBackground2,
        borderColor: tokens.colorBrandStroke1,
        boxShadow: `inset 0 0 0 ${tokens.strokeWidthThick} ${tokens.colorBrandStroke2}`,
    },
    columnHeader: {
        alignItems: 'center',
        borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
        display: 'flex',
        justifyContent: 'space-between',
        padding: tokens.spacingVerticalS,
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
        minHeight: '5rem',
        overflowY: 'auto',
        padding: tokens.spacingVerticalS,
        scrollbarWidth: 'thin',
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground1,
        border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
        cursor: 'grab',
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        padding: tokens.spacingVerticalS,
        ':active': {
            cursor: 'grabbing',
        },
    },
    cardReadOnly: {
        cursor: 'default',
    },
    cardHeader: {
        alignItems: 'flex-start',
        display: 'flex',
        gap: tokens.spacingHorizontalXS,
        justifyContent: 'space-between',
    },
    cardDetails: {
        color: tokens.colorNeutralForeground3,
    },
    emptyColumn: {
        color: tokens.colorNeutralForeground3,
        padding: tokens.spacingVerticalM,
        textAlign: 'center',
    },
});

type CasesBoardProps = {
    cases: Case[];
    statuses: CaseStatus[];
    canEditCase: (caseObj: Case) => boolean;
    onStatusChange: (caseObj: Case, status: CaseStatus) => Promise<void>;
    height?: string;
};

const statusesMatch = (first?: CaseStatus, second?: CaseStatus) =>
    first?.systemStatus === second?.systemStatus && first?.status === second?.status;

export const CasesBoard = (props: CasesBoardProps) => {
    const { cases, statuses, canEditCase, onStatusChange, height } = props;
    const navigate = useNavigate();
    const styles = useStyles();
    const boardRef = useRef<HTMLDivElement>(null);
    const panState = useRef<{ pointerId: number; startX: number; scrollLeft: number }>();
    const [draggedCaseId, setDraggedCaseId] = useState<string>();
    const [dragOverStatus, setDragOverStatus] = useState<CaseStatus>();
    const [updatingCaseId, setUpdatingCaseId] = useState<string>();
    const [optimisticStatuses, setOptimisticStatuses] = useState<Record<string, CaseStatus>>({});
    const [isPanning, setIsPanning] = useState(false);
    const visibleStatuses = useMemo(
        () =>
            statuses.filter((status) =>
                status.systemStatus === CaseSystemStatus.closed ||
                cases.some(
                    (caseObj) =>
                        caseObj.status?.systemStatus !== CaseSystemStatus.closed &&
                        (!caseObj.allowedStatuses?.length ||
                            caseObj.allowedStatuses.some((allowedStatus) => statusesMatch(allowedStatus, status))),
                ),
            ),
        [cases, statuses],
    );

    const statusAppliesToCase = (caseObj: Case, status: CaseStatus) =>
        status.systemStatus === CaseSystemStatus.closed ||
        !caseObj.allowedStatuses?.length ||
        (caseObj.allowedStatuses?.some((allowedStatus) => statusesMatch(allowedStatus, status)) ?? false);

    const canMoveToStatus = (caseObj: Case, status: CaseStatus) => {
        const currentStatus = caseObj.caseId ? optimisticStatuses[caseObj.caseId] || caseObj.status : caseObj.status;
        if (!canEditCase(caseObj) || statusesMatch(currentStatus, status)) return false;

        return statusAppliesToCase(caseObj, status);
    };

    const moveCase = async (caseObj: Case, status: CaseStatus) => {
        if (!caseObj.caseId || !canMoveToStatus(caseObj, status) || updatingCaseId === caseObj.caseId) return;

        const caseId = caseObj.caseId;
        const previousOptimisticStatus = optimisticStatuses[caseId];
        setOptimisticStatuses((current) => ({ ...current, [caseId]: status }));
        setUpdatingCaseId(caseObj.caseId);
        try {
            await onStatusChange(caseObj, status);
            setOptimisticStatuses((current) => {
                const next = { ...current };
                delete next[caseId];
                return next;
            });
        } catch {
            setOptimisticStatuses((current) => {
                const next = { ...current };
                if (previousOptimisticStatus) {
                    next[caseId] = previousOptimisticStatus;
                } else {
                    delete next[caseId];
                }
                return next;
            });
        } finally {
            setUpdatingCaseId(undefined);
        }
    };

    const onDragStart = (event: DragEvent<HTMLDivElement>, caseObj: Case) => {
        if (!canEditCase(caseObj) || !caseObj.caseId) {
            event.preventDefault();
            return;
        }

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', caseObj.caseId);
        setDraggedCaseId(caseObj.caseId);
    };

    const onDragOver = (event: DragEvent<HTMLDivElement>, status: CaseStatus) => {
        const draggedCase = cases.find((caseObj) => caseObj.caseId === draggedCaseId);
        if (!draggedCase || !canMoveToStatus(draggedCase, status)) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragOverStatus(status);
    };

    const onDrop = (event: DragEvent<HTMLDivElement>, status: CaseStatus) => {
        event.preventDefault();
        const caseId = event.dataTransfer.getData('text/plain') || draggedCaseId;
        const caseObj = cases.find((item) => item.caseId === caseId);
        setDragOverStatus(undefined);
        setDraggedCaseId(undefined);
        if (caseObj) void moveCase(caseObj, status);
    };

    const onDragEnd = () => {
        setDraggedCaseId(undefined);
        setDragOverStatus(undefined);
    };

    const onBoardPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        const board = boardRef.current;
        if (
            !board ||
            !board.contains(event.target as Node) ||
            event.button !== 0 ||
            (event.target as Element).closest('[data-case-card]')
        ) {
            return;
        }

        panState.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            scrollLeft: board.scrollLeft,
        };
        board.setPointerCapture(event.pointerId);
        setIsPanning(true);
        event.preventDefault();
    };

    const onBoardPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const board = boardRef.current;
        const pan = panState.current;
        if (!board || !pan || pan.pointerId !== event.pointerId) return;

        board.scrollLeft = pan.scrollLeft - (event.clientX - pan.startX);
    };

    const stopBoardPanning = (event: PointerEvent<HTMLDivElement>) => {
        const board = boardRef.current;
        if (board?.hasPointerCapture(event.pointerId)) {
            board.releasePointerCapture(event.pointerId);
        }
        panState.current = undefined;
        setIsPanning(false);
    };

    const draggedCase = cases.find((caseObj) => caseObj.caseId === draggedCaseId);

    return (
        <div
            ref={boardRef}
            className={`${styles.board} ${isPanning ? styles.boardDragging : ''}`}
            style={{ height }}
            onPointerDown={onBoardPointerDown}
            onPointerMove={onBoardPointerMove}
            onPointerUp={stopBoardPanning}
            onPointerCancel={stopBoardPanning}
            data-testid='cases-board'
        >
            {visibleStatuses.map((status) => {
                const statusCases = cases.filter((caseObj) => {
                    const currentStatus = caseObj.caseId
                        ? optimisticStatuses[caseObj.caseId] || caseObj.status
                        : caseObj.status;
                    return statusesMatch(currentStatus, status);
                });
                const isDragOver = statusesMatch(dragOverStatus, status);
                const isDisabled = !!draggedCase && !statusAppliesToCase(draggedCase, status);

                return (
                    <Column
                        className={mergeClasses(
                            styles.column,
                            isDragOver && styles.columnDragOver,
                            isDisabled && styles.columnDisabled,
                        )}
                        noGap
                        key={`${status.systemStatus}-${status.status}`}
                        onDragOver={(event) => onDragOver(event, status)}
                        onDrop={(event) => onDrop(event, status)}
                        aria-label={`${status.status} cases`}
                        aria-disabled={isDisabled}
                        data-testid={`cases-board-column-${status.systemStatus}-${status.status}`}
                    >
                        <Column
                            className={styles.columnHeader}
                            style={{ backgroundColor: systemStatusHeaderColors[status.systemStatus] }}
                            height='auto'
                            padding='pr-3'
                        >
                            <Row justifyContent='space-between'>
                                <Column noGap>
                                    <Text block weight='semibold'>
                                        {status.status}
                                    </Text>
                                    <Text block className={styles.cardDetails} size={200}>
                                        {status.systemStatus}
                                    </Text>
                                </Column>
                                <Column width='auto' vAlign='middle'>
                                    <Text size={200}>{statusCases.length}</Text>
                                </Column>
                            </Row>
                        </Column>
                        <div className={styles.cardList}>
                            {statusCases.map((caseObj) => {
                                const canEdit = canEditCase(caseObj);
                                const isUpdating = updatingCaseId === caseObj.caseId;
                                const availableStatuses = visibleStatuses.filter((targetStatus) =>
                                    canMoveToStatus(caseObj, targetStatus),
                                );

                                return (
                                    <div
                                        className={`${styles.card} ${!canEdit ? styles.cardReadOnly : ''}`}
                                        data-case-card
                                        draggable={canEdit && !isUpdating}
                                        key={caseObj.caseId}
                                        onDragStart={(event) => onDragStart(event, caseObj)}
                                        onDragEnd={onDragEnd}
                                        data-testid={`cases-board-card-${caseObj.caseId}`}
                                    >
                                        <div className={styles.cardHeader}>
                                            <Link
                                                onClick={() => navigate(Paths.viewEditCase(caseObj.caseId as string))}
                                                weight='semibold'
                                            >
                                                {caseObj.caseName}
                                            </Link>
                                            {isUpdating ? (
                                                <Spinner size='tiny' label='Updating case status' />
                                            ) : (
                                                canEdit &&
                                                !!availableStatuses.length && (
                                                    <Menu>
                                                        <MenuTrigger disableButtonEnhancement>
                                                            <Button
                                                                appearance='subtle'
                                                                aria-label={`Move ${caseObj.caseName} to another status`}
                                                                icon={<MoreHorizontalRegular />}
                                                                size='small'
                                                            />
                                                        </MenuTrigger>
                                                        <MenuPopover>
                                                            <MenuList>
                                                                {availableStatuses.map((targetStatus) => (
                                                                    <MenuItem
                                                                        key={`${targetStatus.systemStatus}-${targetStatus.status}`}
                                                                        onClick={() => void moveCase(caseObj, targetStatus)}
                                                                    >
                                                                        Move to {targetStatus.status}
                                                                    </MenuItem>
                                                                ))}
                                                            </MenuList>
                                                        </MenuPopover>
                                                    </Menu>
                                                )
                                            )}
                                        </div>
                                        {!!caseObj.caseType && (
                                            <Text className={styles.cardDetails} size={200}>
                                                {caseObj.caseType}
                                            </Text>
                                        )}
                                        {!!caseObj.retainedBy?.party?.name && (
                                            <Text className={styles.cardDetails} size={200}>
                                                Client: {caseObj.retainedBy.party.name}
                                            </Text>
                                        )}
                                    </div>
                                );
                            })}
                            {!statusCases.length && (
                                <Text className={styles.emptyColumn} size={200}>
                                    No cases
                                </Text>
                            )}
                        </div>
                    </Column>
                );
            })}
        </div>
    );
};
